import {
  Button,
  Calendar,
  CalendarMdIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  TimePicker,
  WarningSmIcon,
} from '@crono-one/design-system';
import { FeConstants } from 'constants/FeConstants';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { format, isSameDay } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  dateToTimeString,
  getDateYearsFromNow,
  setTimeStringToDate,
  toUtcMidnightOfLocalDay,
} from 'utils/dateUtils';

const getNow = () => new Date();

interface IProps {
  /**
   * The dialog has no open state of its own: unmount it from here. Runs on
   * confirm and cancel as well as on the header X, Escape and a backdrop click.
   */
  onClose: () => void;
  /**
   * Runs only when the confirm button is pressed, never on a plain dismissal.
   * With `timeOptional`, `newDate` is the picked day at UTC midnight and the time comes
   * separately as `scheduleTime` — the shape the task patch endpoints read the two from.
   * Otherwise `newDate` carries both, as it always has.
   */
  onConfirm: (newDate: Date, scheduleTime?: string | null) => void;
  /** Runs only when the cancel button is pressed, never on a plain dismissal. */
  onCancel?: () => void;
  isRescheduleLoading?: boolean;
  initialDate?: Date;
  taskType?: TaskTodoType;
  error?: string | null;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  /**
   * Opens with no time picked, so a day can be chosen on its own and whatever is being
   * rescheduled keeps the time it already has.
   */
  timeOptional?: boolean;
}

const RescheduleCalendar = ({
  onClose,
  onConfirm,
  onCancel,
  isRescheduleLoading = false,
  initialDate,
  error,
  title = 'Select date and time',
  confirmText = 'Reschedule',
  cancelText = 'Cancel',
  timeOptional = false,
}: IProps) => {
  const [initialScheduleDate] = useState(() =>
    initialDate ? new Date(initialDate) : getNow(),
  );
  const [scheduleDay, setScheduleDay] = useState<Date>(initialScheduleDate);
  const [scheduleTime, setScheduleTime] = useState<string | null>(() =>
    timeOptional ? null : dateToTimeString(initialScheduleDate),
  );

  const minTime = useMemo(() => {
    const now = getNow();

    return isSameDay(scheduleDay, now) ? dateToTimeString(now) : undefined;
  }, [scheduleDay]);

  const handleChangeScheduleTime = (newTime: string | null) => {
    if (newTime || timeOptional) {
      setScheduleTime(newTime);
    }
  };

  const handleSelectScheduleDate = (newDate: Date) => {
    if (newDate) {
      setScheduleDay(newDate);
    }
  };

  const handleRescheduleTask = () => {
    if (!scheduleTime) {
      if (timeOptional) {
        onConfirm(toUtcMidnightOfLocalDay(scheduleDay), null);
        onClose();
      }
      return;
    }
    const date = setTimeStringToDate(scheduleDay, scheduleTime);
    onConfirm(
      timeOptional ? toUtcMidnightOfLocalDay(date) : date,
      format(date, 'HH:mmxxx'),
    );
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" portalClassName="z-5010">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col">
            <div className="h-fit items-start flex">
              <div className="flex flex-col gap-3 mt-2 mr-3">
                <div className="flex items-center gap-1">
                  <CalendarMdIcon className="size-6" />
                  <p className="text-sm font-medium">
                    {format(scheduleDay, 'dd/MM/yyyy')}
                  </p>
                </div>

                <TimePicker
                  value={scheduleTime}
                  disabled={isRescheduleLoading}
                  min={minTime}
                  onValueChange={handleChangeScheduleTime}
                  showTrigger={false}
                />
              </div>

              <Calendar
                className="mx-auto"
                selected={scheduleDay}
                captionLayout="dropdown"
                disabled={{
                  before: getNow(),
                }}
                startMonth={getNow()}
                endMonth={getDateYearsFromNow(
                  +FeConstants.datePickerDefaultYearsOffset,
                )}
                onSelect={handleSelectScheduleDate}
              />
            </div>

            {error && (
              <div className="w-full flex items-start gap-2 p-2 rounded-lg bg-red-200 text-red-500">
                <WarningSmIcon className="size-4" />
                <span className="text-xs font-medium">{error}</span>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" size="lg" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button
            size="lg"
            onClick={handleRescheduleTask}
            loading={isRescheduleLoading}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleCalendar;
